import React, { useMemo, useState } from 'react';
import {
  Box,
  Button,
  CalendarIcon,
  Menu,
  MenuItem,
  MenuOverlay,
  MoonIcon,
  SettingsIcon,
  StarIcon,
  SunIcon,
  Text,
} from '@razorpay/blade/components';
import type { OvertimeFormula } from 'components/Settings/AttendanceAndLeavesSettings/types';
import {
  buildFormulaDescription,
  computeOtComponentChanges,
} from 'components/Settings/AttendanceAndLeavesSettings/utils';
import {
  OVERTIME_TYPE,
  OVERTIME_TYPE_API_VALUE,
  OVERTIME_TYPE_CARDS,
  OVERTIME_TYPE_FROM_API_VALUE,
  OvertimeTypeValue,
} from 'components/Settings/AttendanceAndLeavesSettings/constants';
import { OvertimeFormulaModal } from './OvertimeFormulaModal';
import { DisableFormulaModal } from './DisableFormulaModal';
import {
  useCreateFormulaCard,
  useCreateFormulaCardTypeMapping,
  useDeleteFormulaCard,
  useOtFormulaCardTypeMappings,
  useBulkUpdateOtBehaviour,
} from './hooks';
import type { OtSalaryComponent } from './hooks/useOtSalaryComponents';

const TYPE_ICONS: Record<OvertimeTypeValue, typeof SunIcon> = {
  [OVERTIME_TYPE.workingDay]: SunIcon,
  [OVERTIME_TYPE.nightTime]: MoonIcon,
  [OVERTIME_TYPE.weeklyOff]: CalendarIcon,
  [OVERTIME_TYPE.publicHoliday]: StarIcon,
};

// Ubitech-integrated orgs get a fixed set of overtime type cards instead of the
// free-form formula list. The type <-> formula association is persisted via the
// OTFormulaCardTypeMapping API (overtimeCreateFormulaCardTypeMapping /
// overtimeListFormulaCardTypeMappings). "Editing" a card's formula creates a new
// formula card, points the mapping at it, then disables the previous formula card
// for that type — formula cards themselves are immutable once mapped.
export const UbitechOvertimeCards = ({
  otComponents,
  formulas,
  onBeforeOpenModal,
}: {
  otComponents: OtSalaryComponent[];
  formulas: OvertimeFormula[];
  onBeforeOpenModal: () => Promise<void>;
}) => {
  const { mutate: createFormula, isLoading: isCreating } = useCreateFormulaCard();
  const { mutate: createTypeMapping } = useCreateFormulaCardTypeMapping();
  const { mutate: deleteFormula } = useDeleteFormulaCard();
  const { mutate: bulkUpdateComponents } = useBulkUpdateOtBehaviour();
  const { data: typeMappings = [] } = useOtFormulaCardTypeMappings();

  const [activeType, setActiveType] = useState<OvertimeTypeValue | null>(null);
  const [formulaToDisable, setFormulaToDisable] = useState<{
    type: OvertimeTypeValue;
    formula: OvertimeFormula;
  } | null>(null);

  const typeFormulas = useMemo(() => {
    const map: Partial<Record<OvertimeTypeValue, OvertimeFormula>> = {};
    typeMappings.forEach((mapping) => {
      const type = OVERTIME_TYPE_FROM_API_VALUE[mapping.overtimeType];
      if (!type) return;
      const formula = formulas.find((f) => f.id === mapping.formulaCardId && f.isActive);
      if (formula) map[type] = formula;
    });
    return map;
  }, [typeMappings, formulas]);

  const editingFormula = activeType !== null ? typeFormulas[activeType] : undefined;

  const openModalForType = async (type: OvertimeTypeValue) => {
    await onBeforeOpenModal();
    setActiveType(type);
  };

  const closeModal = () => {
    setActiveType(null);
  };

  const handleSave = (formula: OvertimeFormula, selectedComponentIds?: string[]) => {
    if (activeType === null) return;
    const type = activeType;
    const previousFormula = typeFormulas[type];
    const overtimeType = OVERTIME_TYPE_API_VALUE[type];

    if (selectedComponentIds !== undefined) {
      const changes = computeOtComponentChanges(otComponents, selectedComponentIds);
      if (changes.length > 0) {
        bulkUpdateComponents(changes);
      }
    }

    createFormula(formula, {
      onSuccess: (response) => {
        const newFormulaCardId = response.overtimeCreateFormulaCard.data.otFormulaCard.id;

        createTypeMapping(
          { overtimeType, formulaCardId: newFormulaCardId },
          {
            onSuccess: () => {
              if (previousFormula) {
                deleteFormula({ id: previousFormula.id, formulaName: previousFormula.name });
              }
              closeModal();
            },
          },
        );
      },
    });
  };

  return (
    <>
      <Box marginTop="spacing.6" display="grid" gridTemplateColumns="1fr 1fr" gap="spacing.4">
        {OVERTIME_TYPE_CARDS.map(({ type, label, description }) => {
          const Icon = TYPE_ICONS[type];
          const formula = typeFormulas[type];

          return (
            <Box
              key={type}
              padding="spacing.5"
              borderRadius="medium"
              borderWidth="thin"
              borderColor="surface.border.gray.subtle"
              backgroundColor="surface.background.gray.moderate">
              <Box display="flex" alignItems="flex-start" justifyContent="space-between">
                <Box display="flex" alignItems="center" gap="spacing.3">
                  <Box
                    padding="spacing.3"
                    borderRadius="medium"
                    backgroundColor="surface.background.primary.subtle">
                    <Icon size="medium" color="interactive.icon.primary.normal" />
                  </Box>
                  <Text weight="semibold">{label}</Text>
                </Box>
                <Menu openInteraction="click">
                  <Button
                    variant="tertiary"
                    size="small"
                    icon={SettingsIcon}
                    accessibilityLabel={`${label} formula actions`}
                    onClick={() => undefined}
                  />
                  <MenuOverlay>
                    {formula ? (
                      <>
                        <MenuItem title="Update formula" onClick={() => openModalForType(type)} />
                        <MenuItem
                          title="Disable formula"
                          description="Permanently disable this formula from payroll calculations."
                          color="negative"
                          onClick={() => setFormulaToDisable({ type, formula })}
                        />
                      </>
                    ) : (
                      <MenuItem title="Create formula" onClick={() => openModalForType(type)} />
                    )}
                  </MenuOverlay>
                </Menu>
              </Box>

              <Box marginTop="spacing.4">
                {formula ? (
                  <>
                    <Text weight="medium">{formula.name}</Text>
                    <Text size="small" color="surface.text.gray.muted">
                      {buildFormulaDescription(formula, otComponents)}
                    </Text>
                  </>
                ) : (
                  <Text size="small" color="surface.text.gray.muted">
                    No formula added yet.
                  </Text>
                )}
              </Box>
              {!formula && (
                <Text marginTop="spacing.2" size="xsmall" color="surface.text.gray.muted">
                  {description}
                </Text>
              )}
            </Box>
          );
        })}
      </Box>

      {activeType !== null && (
        <OvertimeFormulaModal
          isOpen={true}
          onDismiss={closeModal}
          onSave={handleSave}
          otComponents={otComponents}
          isSubmitting={isCreating}
          initialFormula={editingFormula}
        />
      )}

      {formulaToDisable && (
        <DisableFormulaModal
          formula={formulaToDisable.formula}
          onDismiss={() => setFormulaToDisable(null)}
        />
      )}
    </>
  );
};
